/**
 * La vista «Mes» de la Agenda (dirección, 14/10/2026): reglas pequeñas y
 * textos de la rejilla del mes (components/citas/AgendaMes.jsx y
 * pages/CitasPage.jsx).
 *
 * Qué está abierto, qué cerrado y cuántos pacientes hay lo decide el
 * SERVIDOR (backend/services/agenda_mes.py, GET /api/citas/mes): aquí no se
 * recalcula nada, solo se cuenta y se dice con palabras lo que llega.
 */
import { addDaysStr, strToDate } from './citasApi';
import { INICIAL_DIA, MESES } from './disponibilidad';

export { INICIAL_DIA };

/** El motivo de un módulo cerrado porque ese día no tiene horario (el mismo
 *  texto que `SIN_HORARIO` en backend/services/agenda_mes.py). */
export const SIN_HORARIO = 'Sin horario';

export const MODULOS = [
  { clave: 'manana', nombre: 'Mañana', corto: 'M', frase: 'mañana' },
  { clave: 'tarde', nombre: 'Tarde', corto: 'T', frase: 'tarde' },
];

export const DIA_SEMANA_CORTO = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const pad2 = (n) => String(n).padStart(2, '0');

/** «2026-10-14» → «2026-10-01». */
export function primeroDelMes(fecha) {
  return `${String(fecha).slice(0, 7)}-01`;
}

/**
 * Suma `n` meses (puede ser negativo) a una fecha, con el mismo día si existe
 * y, si no, el último del mes (31 de enero + 1 → 28 o 29 de febrero).
 */
export function sumarMeses(fecha, n) {
  const d = strToDate(fecha);
  const destino = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const ultimo = new Date(destino.getFullYear(), destino.getMonth() + 1, 0).getDate();
  return `${destino.getFullYear()}-${pad2(destino.getMonth() + 1)}-${pad2(Math.min(d.getDate(), ultimo))}`;
}

/** «2026-10-01» → «Octubre de 2026». */
export function textoMes(primero) {
  const nombre = MESES[Number(String(primero).slice(5, 7)) - 1] || '';
  return `${nombre.charAt(0).toUpperCase()}${nombre.slice(1)} de ${String(primero).slice(0, 4)}`;
}

/**
 * Las semanas que enseña la rejilla del mes, de lunes a domingo, con los días
 * del mes anterior y del siguiente que completan la primera y la última:
 * [[{fecha, delMes}, …7], …].
 */
export function semanasDelMes(primero) {
  const hueco = (strToDate(primero).getDay() + 6) % 7; // lunes = 0
  const mes = String(primero).slice(0, 7);
  let fecha = addDaysStr(primero, -hueco);
  const semanas = [];
  do {
    const semana = [];
    for (let i = 0; i < 7; i += 1) {
      semana.push({ fecha, delMes: fecha.slice(0, 7) === mes });
      fecha = addDaysStr(fecha, 1);
    }
    semanas.push(semana);
  } while (fecha.slice(0, 7) === mes);
  return semanas;
}

/** ¿Ese módulo está cerrado porque ese día no tiene horario? */
export function sinHorario(celda, modulo) {
  return celda?.[modulo] === 'cerrada' && celda?.[`motivo_${modulo}`] === SIN_HORARIO;
}

/**
 * ¿Hay algo que enseñar de esa agenda ese día? Un módulo abierto (o en
 * parte), uno cerrado por un bloqueo en un día con horario, o pacientes
 * citados. Un día sin horario y sin nadie no tiene nada que decir.
 */
export function tieneAlgo(celda) {
  if (!celda) return false;
  if ((celda.pacientes || 0) > 0) return true;
  return MODULOS.some(({ clave }) => celda[clave] !== 'cerrada' || !sinHorario(celda, clave));
}

/**
 * ¿Esa agenda tiene actividad en el mes? (Abierta algún día, aunque sea en
 * parte, o con algún paciente citado.) Es lo que decide qué agendas salen en
 * el mes con «Todas».
 */
export function conActividadEnElMes(agendaId, datos) {
  return Object.values(datos?.dias || {}).some((celdas) => {
    const c = celdas?.[agendaId];
    if (!c) return false;
    return (c.pacientes || 0) > 0 || MODULOS.some(({ clave }) => c[clave] !== 'cerrada');
  });
}

export function textoPacientes(n) {
  if (!n) return 'sin pacientes';
  return n === 1 ? '1 paciente' : `${n} pacientes`;
}

/** «mañana abierta, 6 pacientes», «tarde cerrada, Festivo», «tarde sin
 *  consulta», «mañana abierta en parte (Congreso), 2 pacientes». */
export function textoModulo(celda, modulo) {
  const { clave, frase } = modulo;
  const estado = celda?.[clave];
  const n = celda?.[`pacientes_${clave}`] || 0;
  const motivo = celda?.[`motivo_${clave}`];
  if (estado === 'abierta') return `${frase} abierta, ${textoPacientes(n)}`;
  if (estado === 'parcial') {
    return `${frase} abierta en parte${motivo ? ` (bloqueo: ${motivo})` : ''}, ${textoPacientes(n)}`;
  }
  const base = sinHorario(celda, clave) ? `${frase} sin consulta` : `${frase} cerrada, ${motivo || 'bloqueada'}`;
  // Cerrada con alguien citado (un bloqueo puesto después, una cita forzada
  // fuera de horario): se dice, que hay que avisarle.
  if (!n) return base;
  return `${base}, ${n === 1 ? '1 paciente citado' : `${n} pacientes citados`}`;
}

/** Lo de una agenda un día, en una frase: «mañana …; tarde …» o «sin
 *  consulta» si no tiene nada. */
export function textoCelda(celda) {
  if (!celda) return 'sin datos';
  if (!tieneAlgo(celda)) return 'sin consulta';
  return MODULOS.map((m) => textoModulo(celda, m)).join('; ');
}

/** El porqué que se ve en la casilla, si algo está cerrado o en parte por un
 *  bloqueo: «Festivo» con el día entero cerrado, «Tarde: Congreso» si es
 *  solo un módulo. Sin bloqueos, null. */
export function motivoVisible(celda) {
  if (!celda) return null;
  if (celda.motivo_cerrada && celda.motivo_cerrada !== SIN_HORARIO) return celda.motivo_cerrada;
  const partes = MODULOS
    .filter(({ clave }) => celda[clave] !== 'abierta' && celda[`motivo_${clave}`]
      && celda[`motivo_${clave}`] !== SIN_HORARIO)
    .map(({ clave, nombre }) => `${nombre}: ${celda[`motivo_${clave}`]}`);
  return partes.length ? partes.join(' · ') : null;
}

/** «Consulta» → «Cons.», «Quirófano» → «Qx». */
export function abreviar(nombre) {
  return String(nombre || '')
    .replace(/\bconsulta\b/gi, 'Cons.')
    .replace(/quir[oó]fano/gi, 'Qx')
    .replace(/\s+/g, ' ')
    .trim();
}

const TITULO = /^(dr|dra|doctor|doctora)\.?$/i;
const PALABRA_TIPO = /^(consulta|quir[oó]fano)$/i;
const palabras = (texto) => String(texto || '').split(/\s+/).filter(Boolean);

/** «Qx» si la agenda es de quirófano (por su tipo o, si no lo tiene, por su
 *  nombre); null si es de consulta, que es lo normal y no hace falta
 *  decirlo. */
function distintivoTipo(agenda) {
  const tipo = String(agenda?.tipo || '').toLowerCase();
  if (tipo === 'quirofano' || (!tipo && /quir[oó]fano/i.test(String(agenda?.name || '')))) return 'Qx';
  return null;
}

/** El apellido del profesional: lo que va detrás de «Dr.»/«Dra.» en el nombre
 *  de la agenda o, si no, su `doctor_name` sin el título y sin el nombre de
 *  pila (con una sola palabra, esa). null si la agenda no es de nadie. */
function apellidoDe(agenda) {
  const enNombre = palabras(agenda?.name);
  const i = enNombre.findIndex((p) => TITULO.test(p));
  if (i >= 0 && i < enNombre.length - 1) return enNombre.slice(i + 1).join(' ');
  const pro = palabras(agenda?.doctor_name).filter((p) => !TITULO.test(p));
  if (pro.length >= 2) return pro.slice(1).join(' ');
  return pro[0] || null;
}

/**
 * El nombre corto de una agenda en una casilla del mes con varias agendas
 * (revisión del 14/10/2026: con «Cons. Dra. …» recortado, todas empezaban
 * igual y no se distinguían). Empieza por lo que la distingue, el apellido
 * del profesional, y el tipo va aparte, en un distintivo pequeño que no se
 * come el recorte: «Qx» en las de quirófano; las de consulta, sin nada (con
 * «Cons.» también, a 1024 px el apellido se quedaba en 30 px).
 * {principal, tipo}. Una agenda sin profesional («Enfermería») va por su
 * nombre, sin la palabra del tipo si la lleva. El nombre entero va en el
 * título y en lo que lee el lector de pantalla.
 */
export function nombreCorto(agenda) {
  const tipo = distintivoTipo(agenda);
  const apellido = apellidoDe(agenda);
  if (apellido) return { principal: apellido, tipo };
  const resto = palabras(agenda?.name).filter((p) => !PALABRA_TIPO.test(p));
  if (!resto.length) return { principal: abreviar(agenda?.name), tipo: null };
  return { principal: resto.join(' '), tipo };
}

/**
 * Los nombres cortos de varias agendas, {id: {principal, tipo}}. Si dos
 * quedan iguales (dos consultas del mismo médico), esas van por su nombre
 * abreviado, que sí las distingue.
 */
export function nombresCortos(agendas = []) {
  const cortos = Object.fromEntries((agendas || []).map((a) => [a.id, nombreCorto(a)]));
  const clave = (c) => `${c.principal}|${c.tipo || ''}`.toLowerCase();
  const veces = {};
  Object.values(cortos).forEach((c) => { veces[clave(c)] = (veces[clave(c)] || 0) + 1; });
  (agendas || []).forEach((a) => {
    if (veces[clave(cortos[a.id])] > 1) cortos[a.id] = { principal: abreviar(a.name), tipo: null };
  });
  return cortos;
}
